/**
 * End of day (Z report): one restaurant day on its own clock - sales, tax, tips,
 * discounts, refunds, money taken per tender, and every cash drawer with its
 * expected / counted / over-short. Figures are the servers': the sales overview and
 * tender report (reports-service) for the day, the drawers (backend /api/cash-drawers).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Printer, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const c = (cents) => fmt((cents || 0) / 100);
const signed = (cents) => `${cents < 0 ? '-' : ''}${c(Math.abs(cents || 0))}`;
const when = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—');

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function Line({ label, value, strong, testid }) {
  return (
    <div className={`flex justify-between py-1 ${strong ? 'font-semibold text-gray-900 border-t border-gray-200 mt-1 pt-2' : 'text-gray-700'}`}>
      <span>{label}</span><span data-testid={testid}>{value}</span>
    </div>
  );
}

export default function EndOfDayReport({ token }) {
  const [day, setDay] = useState(localToday());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const headers = { Authorization: `Bearer ${token}` };
    const q = `period=custom&start_date=${day}&end_date=${day}`;
    try {
      const [sales, tenders, drawers] = await Promise.all([
        axios.get(`${API}/reports/sales-overview?${q}`, { headers }),
        axios.get(`${API}/reports/tender-card-types?${q}`, { headers }),
        axios.get(`${API}/cash-drawers?date=${day}`, { headers }),
      ]);
      setData({ sales: sales.data, tenders: tenders.data, drawers: drawers.data });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not load the end-of-day report.');
    } finally {
      setLoading(false);
    }
  }, [token, day]);

  useEffect(() => { load(); }, [load]);

  const s = data?.sales;
  const t = data?.tenders;
  const dr = data?.drawers;
  return (
    <div className="space-y-4" data-testid="end-of-day-tab">
      <style>{'@media print { body * { visibility: hidden; } #eod-print, #eod-print * { visibility: visible; } #eod-print { position: absolute; left: 0; top: 0; width: 100%; } }'}</style>
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-500" />
        <input type="date" value={day} max={localToday()} onChange={(e) => e.target.value && setDay(e.target.value)}
          className="border border-gray-300 rounded-md px-2 h-9 text-sm text-gray-900" data-testid="eod-date" />
        <Button variant="outline" size="sm" onClick={() => window.print()} disabled={!data} data-testid="eod-print">
          <Printer className="h-4 w-4 mr-1" /> Print
        </Button>
      </div>
      {loading && <p className="text-gray-500 text-sm">Loading…</p>}
      {error && <p className="text-red-600 text-sm" data-testid="eod-error">{error}</p>}
      {data && !loading && (
        <div id="eod-print" className="grid gap-4 md:grid-cols-2">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Sales · {day}</h3>
            <Line label="Orders" value={s.total_orders} testid="eod-orders" />
            <Line label="Gross sales" value={fmt(s.gross_sales)} />
            <Line label="Discounts" value={`−${fmt(s.total_discounts)}`} />
            <Line label="Refunds" value={`−${fmt(s.total_refunds)}`} />
            <Line label="Voids" value={`−${fmt(s.void_amount)}`} />
            <Line label="Net sales" value={fmt(s.net_sales)} strong />
            <Line label="Tax collected" value={fmt(s.total_tax)} />
            <Line label="Tips" value={fmt(s.total_tips)} />
            <Line label="Money taken" value={fmt(s.money_taken)} strong testid="eod-money-taken" />
            <Line label="Revenue (after refunds)" value={fmt(s.revenue)} />
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-2">By tender</h3>
            {(t.by_payment_method || []).length === 0 && <p className="text-gray-500 text-sm">No payments this day.</p>}
            {(t.by_payment_method || []).map((m) => (
              <Line key={m.method} label={`${m.method.replace('_', ' ')} (${m.count})`} value={fmt(m.amount)} testid={`eod-tender-${m.method}`} />
            ))}
            <Line label={`Card refunds (${t.card_refunds?.count || 0})`} value={`−${fmt(t.card_refunds?.amount)}`} />
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4 md:col-span-2">
            <h3 className="font-semibold text-gray-900 mb-2">Cash drawers</h3>
            {dr.drawers.length === 0 && <p className="text-gray-500 text-sm" data-testid="eod-no-drawers">No drawer was opened this day.</p>}
            {dr.drawers.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-gray-700" data-testid="eod-drawers">
                  <thead><tr className="text-left text-gray-500">
                    <th className="py-1 pr-3">Drawer</th><th className="pr-3">Open / close</th><th className="pr-3 text-right">Start</th>
                    <th className="pr-3 text-right">Cash sales</th><th className="pr-3 text-right">In / out</th><th className="pr-3 text-right">Refunds</th>
                    <th className="pr-3 text-right">Expected</th><th className="pr-3 text-right">Counted</th><th className="text-right">Over / short</th>
                  </tr></thead>
                  <tbody>
                    {dr.drawers.map((x) => (
                      <tr key={x.id} className="border-t border-gray-100">
                        <td className="py-1.5 pr-3">{x.name}<div className="text-xs text-gray-500">{x.opened_by}{x.closed_by && x.closed_by !== x.opened_by ? ` → ${x.closed_by}` : ''}</div></td>
                        <td className="pr-3">{when(x.opened_at)} – {x.status === 'open' ? <span className="text-amber-600 font-medium">still open</span> : when(x.closed_at)}</td>
                        <td className="pr-3 text-right">{c(x.opening_cents)}</td>
                        <td className="pr-3 text-right">{c(x.cash_sales_cents + x.cash_tips_cents)}</td>
                        <td className="pr-3 text-right">{c(x.paid_in_cents)} / {c(x.paid_out_cents)}</td>
                        <td className="pr-3 text-right">{c(x.cash_refunds_cents)}</td>
                        <td className="pr-3 text-right">{c(x.expected_cents)}</td>
                        <td className="pr-3 text-right">{x.status === 'closed' ? c(x.counted_cents) : '—'}</td>
                        <td className={`text-right font-medium ${x.over_short_cents < 0 ? 'text-red-600' : x.over_short_cents > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                          {x.status === 'closed' ? `${x.over_short_cents < 0 ? '−' : x.over_short_cents > 0 ? '+' : ''}${c(Math.abs(x.over_short_cents))}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Line label="Total over / short (closed drawers)" value={signed(dr.totals.over_short_cents)} strong testid="eod-over-short" />
            {dr.outside_drawers.cash_sales_count > 0 && (
              <p className="text-sm text-amber-700 mt-2" data-testid="eod-outside">
                {c(dr.outside_drawers.cash_sales_cents + dr.outside_drawers.cash_tips_cents)} cash ({dr.outside_drawers.cash_sales_count} payments) was taken with no drawer open on that register.
              </p>
            )}
            {dr.totals.open > 0 && <p className="text-sm text-amber-700 mt-1">{dr.totals.open} drawer(s) still open - close them on the register to finish the day.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
